import { FC, useState, Dispatch, SetStateAction, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Step, StepLabel, Stepper } from '@mui/material';

import { PostStepperDto } from '@libs/dto';
import { FormProvider, useForm, useConfirmation, useSnackbar } from '@carcareers/contexts';
import {
  RooftopStepForm,
  JobStepForm,
  JobPreviewStepForm,
  DashboardPageContainer,
  EmployerStepForm,
  AdminPaymentStepForm,
} from '@carcareers/components';
import { useAdminCreatePostMutation } from '@carcareers/store';
import { getErrors, routes } from '@carcareers/utils';

export const PostAdd: FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Select Employer', 'Rooftop Details', 'Job Details', 'Preview', 'Payment & Details'];
  const initialData = { payment: { renew: true } };

  return (
    <DashboardPageContainer idReplacement="Create a new Post">
      <FormProvider initialData={initialData}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, height: '100%' }}>
          <Stepper
            activeStep={activeStep}
            sx={{ backgroundColor: 'background.paper', borderRadius: 3, py: 4, px: 2 }}
          >
            {steps.map((label, index) => (
              <Step key={label} onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box
            sx={{
              p: 3,
              gap: 2,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}
          >
            {activeStep === 0 && <EmployerStepForm />}
            {activeStep === 1 && <RooftopStepForm rooftopName="rooftop" />}
            {activeStep === 2 && <JobStepForm rooftopName="rooftop" />}
            {activeStep === 3 && <JobPreviewStepForm />}
            {activeStep === 4 && <AdminPaymentStepForm />}

            <StepNav activeStep={activeStep} setActiveStep={setActiveStep} />
          </Box>
        </Box>
      </FormProvider>
    </DashboardPageContainer>
  );
};



const StepNav = ({
  activeStep,
  setActiveStep,
}: {
  activeStep: number;
  setActiveStep: Dispatch<SetStateAction<number>>;
}) => {
  const navigate = useNavigate();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const { data, setErrors } = useForm<PostStepperDto>();

  const [createJob] = useAdminCreatePostMutation();

  useEffect(() => {
    if (data) {
      setErrors([]);
    }
  }, [data, setErrors]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleSubmit = async () => {
    const confirmed = await confirm({
      title: 'Confirm Job Posting',
      message: 'Are you sure you want to post this job?',
    });

    if (confirmed) {
      const response = await createJob(data);
      const errors = getErrors(response);

      const employerError = errors.find((error) => error.property.includes('employer'));
      const rooftopError = errors.find((error) => error.property.includes('rooftop'));
      const jobError = errors.find((error) => error.property.includes('job'));
      const paymentError = errors.find((error) => error.property.includes('payment'));
      const promoError = errors.find((error) => error.property.includes('promo'));

      if (errors.length > 0) {
        setErrors(errors);
        if (employerError) {
          setActiveStep(0);
        } else if (rooftopError) {
          setActiveStep(1);
        } else if (jobError) {
          setActiveStep(2);
        } else if (paymentError) {
          setActiveStep(4);
        } else if (promoError) {
          toggleSnackbar('error', promoError.errors[0]);
        }
      } else {
        navigate(`/${routes.ADMIN_HOME}/${routes.ADMIN_POSTS}`);
        toggleSnackbar('success', 'Job posted successfully');
      }
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'flex-end',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse' },
        [theme.containerQueries('drawer').down(500)]: { flexDirection: 'column-reverse' },
      })}
    >
      <Button size="large" variant="outlined" color="secondary" onClick={handleBack}>
        {activeStep === 0 ? 'Cancel' : 'Back'}
      </Button>

      {activeStep >= 0 && activeStep <= 2 && (
        <Button size="large" variant="contained" color="primary" onClick={handleNext}>
          Next
        </Button>
      )}

      {activeStep === 3 && (
        <Button size="large" variant="contained" color="primary" onClick={handleNext}>
          Go to Payment
        </Button>
      )}

      {activeStep === 4 && (
        <Button size="large" variant="contained" color="primary" onClick={handleSubmit}>
          Post Job
        </Button>
      )}
    </Box>
  );
};

export default PostAdd;
